import React, { useEffect, useMemo, useState } from "react";
import Calendar from "react-calendar";
import { useTasks } from "../context/TaskContext";
import { useJournalEntries } from "../hooks/useJournalEntries";
import "./Calendar.css";

const toDisplayDate = (dateId) => {
  if (!dateId) return "Unscheduled";
  const date = new Date(dateId);
  if (Number.isNaN(date.getTime())) return dateId;
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const CalendarPage = () => {
  const {
    user,
    selectedDate,
    setSelectedDate,
    listSummaries,
    formatDateId,
  } = useTasks();
  const { summaries: journalSummaries, entriesByDate } = useJournalEntries();
  const [calendarValue, setCalendarValue] = useState(() =>
    selectedDate ? new Date(selectedDate) : new Date()
  );

  useEffect(() => {
    if (selectedDate) {
      const nextValue = new Date(selectedDate);
      if (!Number.isNaN(nextValue.getTime())) {
        setCalendarValue(nextValue);
      }
    }
  }, [selectedDate]);

  const handleDayClick = async (date) => {
    const dateKey = formatDateId(date);
    setCalendarValue(date);
    if (!dateKey) return;
    await setSelectedDate(dateKey, { ensureList: true });
  };

  const selectedTaskSummary = listSummaries[selectedDate] || {
    todos: 0,
    completed: 0,
  };
  const selectedJournalEntries = entriesByDate[selectedDate] || [];

  const tileContent = ({ date, view }) => {
    if (view !== "month") return null;
    const dateKey = formatDateId(date);
    if (!dateKey) return null;
    const taskSummary = listSummaries[dateKey];
    const journalCount = journalSummaries[dateKey] || 0;
    const taskCount = taskSummary
      ? taskSummary.todos + taskSummary.completed
      : 0;

    if (!taskCount && !journalCount) return null;

    return (
      <div className="calendar-badges">
        {taskCount ? (
          <span className="calendar-badge calendar-badge--tasks" title={`Tasks: ${taskSummary.todos} open, ${taskSummary.completed} done`}>
            {taskCount}
          </span>
        ) : null}
        {journalCount ? (
          <span className="calendar-badge calendar-badge--journal" title={`Journal entries: ${journalCount}`}>
            {journalCount}
          </span>
        ) : null}
      </div>
    );
  };

  const tileClassName = ({ date, view }) => {
    if (view !== "month") return undefined;
    const dateKey = formatDateId(date);
    if (selectedDate && dateKey === selectedDate) {
      return "calendar-selected";
    }
    return undefined;
  };

  const friendlyDate = useMemo(() => toDisplayDate(selectedDate), [selectedDate]);

  return (
    <div className="app-layout">
      <header className="page-header">
        <div className="page-header__content">
          <h1>Planner calendar</h1>
          <p>
            Choose a day to queue tasks and jot down journal notes. Days with
            coloured badges already have something waiting.
          </p>
        </div>
        <div className="page-header__aside">
          {user ? (
            <span className="badge">Signed in</span>
          ) : (
            <span className="badge">Sign in to sync</span>
          )}
        </div>
      </header>

      <section className="page-section calendar-card">
        <div className="page-section__headline" style={{ marginBottom: "18px" }}>
          <h2>Schedule</h2>
          <span className="pill">Planning {friendlyDate}</span>
        </div>
        <div className="calendar-container">
          <Calendar
            onChange={setCalendarValue}
            value={calendarValue}
            onClickDay={handleDayClick}
            className="react-calendar"
            tileContent={tileContent}
            tileClassName={tileClassName}
          />
        </div>
      </section>

      <section className="page-section page-section--subtle">
        <div className="page-section__headline">
          <h2>Day overview</h2>
        </div>
        <div className="stack stack--dense">
          <div className="stack stack--dense">
            <strong>Tasks</strong>
            <span className="muted">
              {selectedTaskSummary.todos + selectedTaskSummary.completed > 0
                ? `${selectedTaskSummary.todos} active · ${selectedTaskSummary.completed} completed`
                : "No tasks scheduled yet."}
            </span>
          </div>
          <div className="divider" />
          <div className="stack stack--dense">
            <strong>Journal entries</strong>
            {selectedJournalEntries.length ? (
              <ul className="stack stack--dense" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {selectedJournalEntries.map((entry) => (
                  <li key={entry.id} className="muted">
                    {entry.Entry}
                  </li>
                ))}
              </ul>
            ) : (
              <span className="muted">No journal entries logged.</span>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CalendarPage;
